import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {GetPipelineStepShape} from 'pipelines/PipelineTypes';
import {GetPipelineConfigSpecType, isValueValid, StepConfigShape} from 'pipelines/StepShapeUtils';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBanner from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import Modal from 'ui-library/modals/Modal';
import {assertJSONObject, JSONValue} from 'util/Types';

import StepConfigEditorRouter from './editors/StepConfigEditorRouter';
import jsonSpecToInitialValue from './jsonSpecToInitialValue';
import {StepConfigEditorContext} from './StepConfigEditorContext';
import {getFieldSettingsIfPresent, ShapeUISchema} from './StepConfigEditorTypes';
import {
  ConfigValidationFunction,
  validateAggregateConfig,
  validateBinDataConfig,
  validateComputedColumnConfig,
  validateRenameColumnsConfig,
  validateReorderColumnsConfig,
  validateFindAndReplaceConfig,
  validateHideColumnsConfig,
  validatePivotConfig,
  validateRegressionInterpolationConfig,
  validateRetypeColumnsConfig,
  validateSortConfig,
  validateUnpivotConfig,
  validateWindowFunctionsConfig,
  validateParameterOptimizationConfigurationConfig,
} from './StepConfigValidationFunctions';
import {
  GetConfigShapeUISchema,
  AGGREGATE_DATASET_V0_UI_SCHEMA,
  BIN_DATA_V0_UI_SCHEMA,
  COMPUTED_COLUMN_V0_UI_SCHEMA,
  FIND_AND_REPLACE_V0_UI_SCHEMA,
  HIDE_COLUMNS_V0_UI_SCHEMA,
  PARAMETER_OPTIMIZATION_CONFIGURATION_V0_UI_SCHEMA,
  PIVOT_DATASET_V0_UI_SCHEMA,
  RENAME_COLUMNS_V0_UI_SCHEMA,
  REORDER_COLUMNS_V0_UI_SCHEMA,
  RETYPE_COLUMNS_V0_UI_SCHEMA,
  SORT_COLUMNS_V0_UI_SCHEMA,
  UNPIVOT_DATASET_V0_UI_SCHEMA,
  WINDOW_FUNCTIONS_V0_UI_SCHEMA,
  REGRESSION_INTERPOLATION_V0_UI_SCHEMA,
} from './StepUISchemas';

function makeInitialConfigState(
  configShape: StepConfigShape,
  uiSchema: ShapeUISchema
): Record<string, JSONValue> {
  return objectFromEntries(
    Object.entries(configShape).map(([variableName, jsonSpec]) => [
      variableName,
      jsonSpecToInitialValue(jsonSpec, getFieldSettingsIfPresent(uiSchema, variableName)),
    ])
  );
}

const FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT =
  'The configuration for this transformation is no longer valid.';

/** @package */
export const FAILED_STEP_WARNING_BANNER_TEXT = `${FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT} Please reconfigure it.`;

/** @package */
export const UPDATES_SYSTEM_COLUMN_NAMES_WARNING_BANNER_TEXT =
  'This transformation edits existing columns, which may cause transforms that reference those columns to error.  You can fix this by editing the column references downstream.';

/** @package */
export const MODAL_SUPPORTED_STEP_TYPES = {
  [PipelineStepType.AGGREGATE_DATASET]: [0],
  [PipelineStepType.BIN_DATA]: [0],
  [PipelineStepType.COMPUTED_COLUMN]: [0],
  [PipelineStepType.FIND_AND_REPLACE]: [0],
  [PipelineStepType.HIDE_COLUMNS]: [0],
  [PipelineStepType.PIVOT_DATASET]: [0],
  [PipelineStepType.REGRESSION_INTERPOLATION]: [0],
  [PipelineStepType.RENAME_COLUMNS]: [0],
  [PipelineStepType.REORDER_COLUMNS]: [0],
  [PipelineStepType.RETYPE_COLUMNS]: [0],
  [PipelineStepType.SORT_COLUMNS]: [0],
  [PipelineStepType.UNPIVOT_DATASET]: [0],
  [PipelineStepType.WINDOW_FUNCTIONS]: [0],
  [PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION]: [0],
} as const satisfies Partial<Record<PipelineStepType, ReadonlyArray<number>>>;

export type StepConfigModalSupportedStepType = keyof typeof MODAL_SUPPORTED_STEP_TYPES;

const UPDATES_SYSTEM_COLUMN_NAMES_STEP_TYPES = [
  PipelineStepType.AGGREGATE_DATASET,
  PipelineStepType.BIN_DATA,
  PipelineStepType.COMPUTED_COLUMN,
  PipelineStepType.HIDE_COLUMNS,
  PipelineStepType.PIVOT_DATASET,
  PipelineStepType.UNPIVOT_DATASET,
  PipelineStepType.WINDOW_FUNCTIONS,
] as const satisfies ReadonlyArray<StepConfigModalSupportedStepType>;

type UpdatesSystemColumnNamesStepType = (typeof UPDATES_SYSTEM_COLUMN_NAMES_STEP_TYPES)[number];

function isUpdatesSystemColumnNamesStepType(
  stepType: StepConfigModalSupportedStepType
): stepType is UpdatesSystemColumnNamesStepType {
  // @ts-expect-error
  return UPDATES_SYSTEM_COLUMN_NAMES_STEP_TYPES.includes(stepType);
}

type GetSupportedVersions<StepType extends StepConfigModalSupportedStepType> =
  (typeof MODAL_SUPPORTED_STEP_TYPES)[StepType][number];

export function isStepConfigEditorModalSupported(
  stepType: PipelineStepType,
  stepVersion: number
): stepType is StepConfigModalSupportedStepType {
  return (
    // @ts-expect-error
    MODAL_SUPPORTED_STEP_TYPES[stepType]?.includes(stepVersion) ?? false
  );
}

interface StepSettings {
  modalTitle: string;
  /**
   * (config, dataFrameSpec) => Array<error strings>
   *
   * If there are no step-specific things to check, just use `() => []`.
   */
  additionalConfigValidationFn: (config: JSONValue, dataFrameSpec: DataFrameSpec) => Array<string>;

  /**
   * Any additional validation that should be done when the config is still in a draft state, i.e. not all
   * fields are filled.
   */
  additionalDraftConfigValidationFn?: (config: JSONValue, dataFrameSpec: DataFrameSpec) => Array<string>;

  /**
   * The UI Schema determines client-side behavior that should be customized per-step-type.
   */
  uiSchema: ShapeUISchema;
}

export const STEP_CONFIG_EDITOR_SETTINGS: Record<
  StepConfigModalSupportedStepType,
  Record<number, StepSettings>
> = {
  [PipelineStepType.AGGREGATE_DATASET]: {
    0: {
      modalTitle: 'Add aggregation',
      additionalConfigValidationFn: validateAggregateConfig,
      uiSchema: AGGREGATE_DATASET_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.BIN_DATA]: {
    0: {
      modalTitle: 'Bin data',
      additionalConfigValidationFn: validateBinDataConfig,
      uiSchema: BIN_DATA_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.COMPUTED_COLUMN]: {
    0: {
      modalTitle: 'Add column',
      // Validation needs to be done in draft state, because ast will always be null when invalid
      additionalConfigValidationFn: () => [],
      additionalDraftConfigValidationFn: validateComputedColumnConfig,
      uiSchema: COMPUTED_COLUMN_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.FIND_AND_REPLACE]: {
    0: {
      modalTitle: 'Find and replace text',
      additionalConfigValidationFn: validateFindAndReplaceConfig,
      uiSchema: FIND_AND_REPLACE_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.HIDE_COLUMNS]: {
    0: {
      modalTitle: 'Hide columns',
      additionalConfigValidationFn: validateHideColumnsConfig,
      uiSchema: HIDE_COLUMNS_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.PIVOT_DATASET]: {
    0: {
      modalTitle: 'Add pivot',
      additionalConfigValidationFn: validatePivotConfig,
      uiSchema: PIVOT_DATASET_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.REGRESSION_INTERPOLATION]: {
    0: {
      modalTitle: 'Calculate regression interpolation',
      additionalConfigValidationFn: validateRegressionInterpolationConfig,
      uiSchema: REGRESSION_INTERPOLATION_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.RENAME_COLUMNS]: {
    0: {
      modalTitle: 'Rename columns',
      additionalConfigValidationFn: validateRenameColumnsConfig,
      uiSchema: RENAME_COLUMNS_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.REORDER_COLUMNS]: {
    0: {
      modalTitle: 'Reorder columns',
      additionalConfigValidationFn: validateReorderColumnsConfig,
      uiSchema: REORDER_COLUMNS_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.SORT_COLUMNS]: {
    0: {
      modalTitle: 'Add sort by',
      additionalConfigValidationFn: validateSortConfig,
      uiSchema: SORT_COLUMNS_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.RETYPE_COLUMNS]: {
    0: {
      modalTitle: 'Convert column format',
      additionalConfigValidationFn: validateRetypeColumnsConfig,
      uiSchema: RETYPE_COLUMNS_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.UNPIVOT_DATASET]: {
    0: {
      modalTitle: 'Add unpivot',
      additionalConfigValidationFn: validateUnpivotConfig,
      uiSchema: UNPIVOT_DATASET_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.WINDOW_FUNCTIONS]: {
    0: {
      modalTitle: 'Add window functions',
      additionalConfigValidationFn: validateWindowFunctionsConfig,
      uiSchema: WINDOW_FUNCTIONS_V0_UI_SCHEMA,
    },
  },
  [PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION]: {
    0: {
      modalTitle: 'Parameter optimization configuration',
      additionalConfigValidationFn: validateParameterOptimizationConfigurationConfig,
      uiSchema: PARAMETER_OPTIMIZATION_CONFIGURATION_V0_UI_SCHEMA,
    },
  },
  // use `satisfies` instead of an explicit type annotation to make it easier to work with `STEP_SETTINGS`
  // while not losing any type safety or autocompletion
} satisfies {
  [StepType in StepConfigModalSupportedStepType]: {
    [StepVersion in GetSupportedVersions<StepType>]: {
      modalTitle: string;
      additionalConfigValidationFn: ConfigValidationFunction<StepType, StepVersion>;
      additionalDraftConfigValidationFn?: ConfigValidationFunction<StepType, StepVersion, 'draft'>;
      uiSchema?: GetConfigShapeUISchema<GetPipelineStepShape<StepType, StepVersion>['config']>;
    };
  };
};

function getTopLevelConfigShape(
  stepType: StepConfigModalSupportedStepType,
  stepVersion: number
): GetPipelineConfigSpecType<'shape'> {
  const configVariablesShape: StepConfigShape = PIPELINE_STEP_SHAPES[stepType][stepVersion].config;
  assert(
    configVariablesShape != null,
    `Config shape should not be null for stepType ${stepType} and stepVersion ${stepVersion}`
  );

  const configShape: GetPipelineConfigSpecType<'shape'> = {
    kind: 'shape',
    fields: configVariablesShape,
    required: true,
  };
  return configShape;
}

interface ValidateConfigProps {
  stepType: StepConfigModalSupportedStepType;
  stepVersion: number;
  config: Record<string, JSONValue>;
  inputDataFrameSpec: DataFrameSpec;
}

function validateConfig({stepType, stepVersion, config, inputDataFrameSpec}: ValidateConfigProps): {
  isComplete: boolean;
  errors: Array<string>;
} {
  const {additionalConfigValidationFn, additionalDraftConfigValidationFn} =
    STEP_CONFIG_EDITOR_SETTINGS[stepType][stepVersion];

  const errors: Array<string> = [];
  // `additionalDraftConfigValidationFn` supports configs in draft state, which may have intermediate values
  // (e.g. null for required string fields)
  if (additionalDraftConfigValidationFn != null) {
    errors.push(...additionalDraftConfigValidationFn(config, inputDataFrameSpec));
  }
  const isComplete = isValueValid(config, getTopLevelConfigShape(stepType, stepVersion), inputDataFrameSpec);
  if (isComplete) {
    // `additionalConfigValidationFn` only works when configs are fully filled out, e.g. no intermediate values
    errors.push(...additionalConfigValidationFn(config, inputDataFrameSpec));
  }

  return {isComplete, errors};
}

interface StepConfigEditorContentsProps {
  stepType: StepConfigModalSupportedStepType;
  stepVersion: number;
  config: Record<string, JSONValue>;
  setConfig: (newConfig: Record<string, JSONValue>) => void;
  inputDataFrameSpec: DataFrameSpec;
  error?: string;
  stepIsFailed?: boolean;
  readOnly?: boolean;
}

export function StepConfigEditorContents({
  stepType,
  stepVersion,
  inputDataFrameSpec,
  config,
  setConfig,
  error,
  stepIsFailed,
  readOnly,
}: StepConfigEditorContentsProps): JSX.Element {
  const {uiSchema} = STEP_CONFIG_EDITOR_SETTINGS[stepType]?.[stepVersion] ?? {};
  const errors = [
    ...(error ? [error] : []),
    ...validateConfig({stepType, stepVersion, config, inputDataFrameSpec}).errors,
  ];

  // Memoize so that the context provider doesn't re-render the entire tree every time this component
  // rerenders e.g. due to a state change (also because we'd get an eslint error otherwise)
  const contextValue = useMemo(() => ({dataFrameSpec: inputDataFrameSpec}), [inputDataFrameSpec]);

  return (
    <StepConfigEditorContext.Provider value={contextValue}>
      <StepConfigEditorRouter
        // Treat top level config shape as a 'shape'
        configShape={getTopLevelConfigShape(stepType, stepVersion)}
        config={config}
        dataTestAttrs={{}}
        onChangeConfig={(config) => {
          // This should be true as long as we have a list of named variables as the step config shape.
          assertJSONObject(config);
          setConfig(config);
        }}
        uiSchema={uiSchema}
        isTopLevel={true}
        disabled={readOnly}
      />
      <InlineBannerStack
        messages={errors.map((error) => ({bannerType: InlineBannerType.WARNING, text: error}))}
      />
      {stepIsFailed && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={readOnly ? FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT : FAILED_STEP_WARNING_BANNER_TEXT}
        />
      )}
    </StepConfigEditorContext.Provider>
  );
}

export interface StepConfigModalProps {
  stepType: StepConfigModalSupportedStepType;
  stepVersion: number;
  initialConfig?: Record<string, JSONValue>;
  inputDataFrameSpec: DataFrameSpec;
  onRequestClose: () => void;
  onRequestSubmit: (config: Record<string, JSONValue>) => void;
  stepIsFailed?: boolean;
  /** The optional props below are defined by InputAwareStepConfigEditorModal */
  error?: string;
  inputIsComplete?: boolean;
  renderStepInputComponent?: (
    config: Record<string, JSONValue>,
    setConfig: (newConfig: Record<string, JSONValue>) => void
  ) => React.ReactNode;
  readOnly?: boolean;
}

export default function StepConfigEditorModal({
  stepType,
  stepVersion,
  initialConfig,
  inputDataFrameSpec,
  onRequestClose,
  onRequestSubmit,
  stepIsFailed,
  error,
  inputIsComplete = true,
  renderStepInputComponent,
  readOnly = false,
}: StepConfigModalProps): JSX.Element {
  const configVariablesShape: StepConfigShape = PIPELINE_STEP_SHAPES[stepType]?.[stepVersion]?.config ?? null;

  assert(
    isStepConfigEditorModalSupported(stepType, stepVersion) && configVariablesShape != null,
    `Config shape should not be null for stepType ${stepType} and stepVersion ${stepVersion}`
  );

  const {modalTitle, uiSchema} = STEP_CONFIG_EDITOR_SETTINGS[stepType]?.[stepVersion] ?? {};
  const [config, setConfig] = useState<Record<string, JSONValue>>(
    initialConfig ?? makeInitialConfigState(configVariablesShape, uiSchema)
  );

  const {isComplete, errors} = validateConfig({stepType, stepVersion, config, inputDataFrameSpec});

  return (
    <Modal
      buttons={[
        {
          submit: true,
          text: 'Submit',
          disabled: !inputIsComplete || !isComplete || errors.length > 0 || readOnly,
        },
      ]}
      dataTestAttrs={{component: 'StepConfigEditorModal', element: 'root'}}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit(config);
      }}
      title={modalTitle}
    >
      {isUpdatesSystemColumnNamesStepType(stepType) && initialConfig != null && (
        <div className={css(styles.banner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={UPDATES_SYSTEM_COLUMN_NAMES_WARNING_BANNER_TEXT}
          />
        </div>
      )}
      {renderStepInputComponent != null && renderStepInputComponent(config, setConfig)}
      <StepConfigEditorContents
        stepType={stepType}
        stepVersion={stepVersion}
        config={config}
        setConfig={setConfig}
        inputDataFrameSpec={inputDataFrameSpec}
        stepIsFailed={stepIsFailed}
        error={error}
        readOnly={readOnly}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  banner: {
    paddingBottom: 8,
  },
});
